import { inject, Signal } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';

import {
  GqlService,
  listPatientProtocolVersionsQuery,
  PatientProtocolType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Maybe } from '@shared/utils/utils';

import { SiteCostType } from '../site-cost.enum';

export const injectSiteProtocolVersions = () => {
  const gqlService = inject(GqlService);
  const mainQuery = inject(MainQuery);
  const overlayService = inject(OverlayService);

  return injectQuery<listPatientProtocolVersionsQuery[], string[]>(() => ({
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    queryFn: async () => {
      const response = await firstValueFrom(gqlService.listPatientProtocolVersions$());

      if (!response.success) {
        throw response.errors;
      }

      return response.data as listPatientProtocolVersionsQuery[];
    },
    queryKey: ['siteProtocolVersions', mainQuery.trialKey()],
  }));
};

export const injectSitePaymentSchedulesBySiteId = ({
  costType,
  protocolVersion,
  siteId,
}: {
  costType: Signal<SiteCostType>;
  protocolVersion: Signal<Maybe<string>>;
  siteId: Signal<Maybe<string>>;
}) => {
  const gqlService = inject(GqlService);
  const mainQuery = inject(MainQuery);
  const overlayService = inject(OverlayService);

  return injectQuery(() => ({
    enabled: !!costType() && !!protocolVersion() && !!siteId(),
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    queryFn: async () => {
      const response = await firstValueFrom(
        gqlService.listSitePaymentSchedules$(
          getPatientProtocolTypes(costType),
          siteId()!,
          protocolVersion()!
        )
      );

      if (!response.success) {
        throw response.errors;
      }

      return response.data!;
    },
    queryKey: [
      'sitePaymentSchedules',
      mainQuery.trialKey(),
      costType(),
      protocolVersion(),
      siteId(),
    ],
  }));
};

export function getPatientProtocolTypes(costType: Signal<SiteCostType>): PatientProtocolType[] {
  const protocolTypeMap = new Map<SiteCostType, PatientProtocolType[]>([
    [
      SiteCostType.PATIENT_INVOICEABLES,
      [
        PatientProtocolType.PATIENT_PROTOCOL_OTHER,
        PatientProtocolType.PATIENT_PROTOCOL_SCREEN_FAIL,
        PatientProtocolType.PATIENT_PROTOCOL_DISCONTINUED,
      ],
    ],
    [SiteCostType.PATIENT_VISITS, [PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT]],
    [SiteCostType.SITE_INVOICEABLES, [PatientProtocolType.PATIENT_PROTOCOL_OVERHEAD]],
  ]);

  return protocolTypeMap.get(costType()) || [];
}
